import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useCallback, useEffect, useMemo, useState } from 'react';

type TDefaultGroupData = {
    headerCheckbox: 'intermediate' | boolean | undefined;
    actKC2Ids: Record<string, boolean>;
    groupName: string | undefined;
};
export const useCommonActsKc2GroupManagerData = <Data, Variables>({
    workOrderId,
    actKC2GroupId,
    getDataQuery,
    handlerCloseEditGroup,
}: {
    workOrderId: number;
    handlerCloseEditGroup: () => void;
    actKC2GroupId: number | null;
    getDataQuery: TypedDocumentNode<Data, Variables>;
}): {
    groupManagerData: Data;
    isLoading: boolean;
    defaultData: TDefaultGroupData | null | undefined;
    groupManagerOpened: boolean;
    handleToggleModalState: () => void;
} => {
    const [groupManagerOpened, setGroupManagerOpened] = useState(false);

    useEffect(() => {
        if (actKC2GroupId) {
            setGroupManagerOpened(true);
        }
    }, [actKC2GroupId, setGroupManagerOpened]);

    const handleToggleModalState = useCallback(() => {
        setGroupManagerOpened((prevState) => !prevState);

        if (actKC2GroupId) {
            handlerCloseEditGroup();
        }
    }, [actKC2GroupId, handlerCloseEditGroup, setGroupManagerOpened]);

    const variables = useMemo<any>(
        () => ({
            workOrderId,
            actKC2GroupId,
        }),
        [actKC2GroupId, workOrderId],
    );

    const [ActKc2List, ActKc2Status] = useRequest<Data, Variables>({
        requestId: 'workOrderActsKC2SelectionList',
        query: getDataQuery,
        variables,
        withEffect: !!(workOrderId && groupManagerOpened),
    });

    const [resetActKc2List] = useResetStoreData({
        requestId: 'workOrderActsKC2SelectionList',
    });

    useEffect(
        () => () => {
            resetActKc2List(undefined);
        },
        [groupManagerOpened, resetActKc2List],
    );

    const isLoading = ActKc2Status === RequestStatus.FETCHING;

    const defaultData = useMemo(() => {
        const datKey = ActKc2List && (Object.keys(ActKc2List)[0] as keyof typeof ActKc2List);

        return groupManagerOpened
            ? (
                  ActKc2List?.[datKey] as {
                      items?: {
                          isIncluded?: boolean;
                          groupName: string;
                          costGroup: any;
                          actId?: number;
                          actKC2Id?: number;
                      }[];
                  }
              )?.items?.reduce(
                  (acc: TDefaultGroupData, item, index, arr) => {
                      if (acc.headerCheckbox === undefined) {
                          const allIncluded = arr.every((i) => i.isIncluded);
                          const someIncluded = arr.some((i) => i.isIncluded);

                          if (allIncluded) {
                              acc.headerCheckbox = true;
                          } else if (someIncluded) {
                              acc.headerCheckbox = 'intermediate';
                          } else {
                              acc.headerCheckbox = false;
                          }
                      }

                      if (!acc.groupName) {
                          acc.groupName = item.groupName ?? '';
                      }
                      if ((item.costGroup && item.actId) || item.actKC2Id) {
                          acc.actKC2Ids[`${item.costGroup}_${item.actId ?? item.actKC2Id}`] = !!item.isIncluded;
                      }

                      return acc;
                  },
                  { headerCheckbox: undefined, actKC2Ids: {}, groupName: undefined },
              )
            : null;
    }, [ActKc2List, groupManagerOpened]);

    return {
        groupManagerData: ActKc2List,
        isLoading,
        defaultData,
        groupManagerOpened,
        handleToggleModalState,
    };
};
